import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { isMockAuthEnabled } from '@/app/msal';
import { fetchMe, ssoLogout, startLogin, type SsoUser } from './ssoClient';
import { mockUser } from './mockUser';
import type { AuthUser } from './types';

export interface UseAuthResult {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isMock: boolean;
  signIn: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<UseAuthResult | null>(null);

// Maps SSO role strings from GET /me → internal Role type used by permissions.ts
const SSO_ROLE_MAP: Record<string, AuthUser['roles'][number]> = {
  'Portal Administrator': 'admin',
  'Tenant Administrator': 'admin',
  'Portal Analyst': 'facilitator',
  'Portal Reader': 'viewer',
  // Entra app-role values as they appear in the access token's `roles` claim.
  'Portal.Administrator': 'admin',
  'Tenant.Administrator': 'admin',
  'Portal.Analyst': 'facilitator',
  'Portal.Reader': 'viewer',
};

function mapSsoUser(raw: SsoUser): AuthUser {
  return {
    id: raw.ObjectId,
    name: raw.DisplayName,
    email: raw.Email ?? raw.Username ?? '',
    tenantId: raw.TenantId,
    roles: raw.Roles.map((r) => SSO_ROLE_MAP[r]).filter((r): r is AuthUser['roles'][number] =>
      Boolean(r),
    ),
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(isMockAuthEnabled ? mockUser : null);
  const [isLoading, setIsLoading] = useState(!isMockAuthEnabled);

  useEffect(() => {
    if (isMockAuthEnabled) return;

    fetchMe()
      .then((raw) => setUser(mapSsoUser(raw)))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const signIn = () => startLogin();

  const signOut = async () => {
    if (!isMockAuthEnabled) await ssoLogout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: Boolean(user),
        isMock: isMockAuthEnabled,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): UseAuthResult {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside <AuthProvider>');
  return ctx;
}
